import classNames from 'classnames';
import { defineMessages, FormattedMessage, useIntl } from 'react-intl';
import Typography from '@alltrails/core/components/Typography';
import LinkSection from './LinkSection';
import styles from './SeoLinks.module.scss';

const SEO_SECTION_STRINGS = defineMessages({
  TRAILS: {
    defaultMessage: 'Top {count} trails'
  },
  PARKS: {
    defaultMessage: 'Top {count} parks'
  },
  CITIES: {
    defaultMessage: 'Top {count} cities'
  },
  REGIONS: {
    defaultMessage: 'Top {count} regions'
  },
  STATES: {
    defaultMessage: 'Top {count} states'
  }
});

type SectionKey = 'REGIONS' | 'STATES' | 'TRAILS' | 'PARKS' | 'CITIES' | 'POIS';

const DEFAULT_MAX_LINKS_PER_SECTION = 15;
const CURATED_HOMEPAGE_MAX_LINKS_PER_SECTION = 50;

export type SeoLinks = {
  key: SectionKey;
  data: {
    url: string;
    title: string;
  }[];
}[];

export type SeoLinksProps = {
  data: SeoLinks;
  className?: string;
  showAdditionalLinks?: boolean;
};

// Desktop: https://www.figma.com/file/deyiqSQ1gfAajm667k20DU/SEO---Increase-internal-links?node-id=54%3A33765
// Mobile: https://www.figma.com/file/deyiqSQ1gfAajm667k20DU/SEO---Increase-internal-links?node-id=8%3A12152
const SeoLinks = ({ data, className, showAdditionalLinks = false }: SeoLinksProps) => {
  const intl = useIntl();
  const maxLinksPerSection = showAdditionalLinks ? CURATED_HOMEPAGE_MAX_LINKS_PER_SECTION : DEFAULT_MAX_LINKS_PER_SECTION;

  return (
    <>
      <div
        className={classNames(styles.container, className, {
          [styles.containerCurated]: showAdditionalLinks
        })}
      >
        <Typography component="h2" variant="heading500">
          <FormattedMessage defaultMessage="Adventure anywhere" />
        </Typography>
        <div className={styles.linkSection}>
          {data.map(section => {
            if (section.data.length === 0) return null;
            // The max number of links sent from the backend is 15, except for homepage curated lists where we send 50 links
            const links = section.data.slice(0, maxLinksPerSection);
            // POIS is only sent for en-US
            const heading =
              section.key === 'POIS'
                ? `Top ${links.length} points of interest`
                : intl.formatMessage(SEO_SECTION_STRINGS[section.key], { count: links.length });
            return <LinkSection links={links} heading={heading} key={section.key} limit={maxLinksPerSection} />;
          })}
        </div>
      </div>
    </>
  );
};

export default SeoLinks;
